import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

const STOCK_VAT_MULTIPLIER = 1.23;
const SKELETON_ROWS = ["alpha", "bravo", "charlie", "delta", "echo", "foxtrot"];

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const normalizePrice = (value: number | null | undefined) => {
	const parsed = Number(value ?? 0);
	return Number.isNaN(parsed) ? 0 : parsed;
};

type MaterialListRow = {
	id: string;
	identifier: string;
	description: string;
	brand: string;
	model: string;
	storeBox?: string | null;
	price?: number | null;
	newStock: number;
	usedStock: number;
	stockMin: number;
	requestedQuantity?: number | null;
};

type MaterialListTableProps = {
	data: MaterialListRow[];
	isLoading: boolean;
	onEdit: (material: MaterialListRow) => void;
	onSell: (material: MaterialListRow) => void;
	onRequest: (material: MaterialListRow) => void;
};

const getStockBadgeClasses = (material: MaterialListRow) => {
	const totalStock = material.newStock + material.usedStock;

	if (totalStock === 0) {
		return "bg-destructive/15 text-destructive";
	}

	if (totalStock <= material.stockMin) {
		return "bg-amber-500/15 text-amber-700";
	}

	return "bg-emerald-500/15 text-emerald-700";
};

const buildDescription = (material: MaterialListRow) =>
	[material.description, material.brand, material.model]
		.map((value) => value.trim())
		.filter((value) => value.length > 0)
		.join(", ") || "-";

export function MaterialListTable({
	data,
	isLoading,
	onEdit,
	onSell,
	onRequest,
}: MaterialListTableProps) {
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow key={`material-skeleton-${rowKey}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-56 rounded bg-muted/70" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-16 rounded bg-muted/60" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-20 rounded bg-muted/50" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-20 rounded bg-muted/40" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-20 rounded bg-muted/40" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-10 rounded bg-muted/30" />
					</TableCell>
					<TableCell className="text-right">
						<div className="ml-auto h-8 w-40 rounded bg-muted/30" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	const renderEmpty = () => (
		<TableBody>
			<TableRow>
				<TableCell
					colSpan={8}
					className="py-10 text-center text-sm text-muted-foreground"
				>
					Nenhum material encontrado.
				</TableCell>
			</TableRow>
		</TableBody>
	);

	const renderRows = () => (
		<TableBody>
			{data.map((material) => {
				const basePrice = normalizePrice(material.price);
				const priceWithVat = basePrice * STOCK_VAT_MULTIPLIER;
				const requested = material.requestedQuantity ?? 0;

				return (
					<TableRow key={material.id}>
						<TableCell className="font-medium text-foreground">
							{material.identifier}
						</TableCell>
						<TableCell>{buildDescription(material)}</TableCell>
						<TableCell className="text-center">
							{material.storeBox?.trim() || "-"}
						</TableCell>
						<TableCell className="text-center">
							{formatCurrency(basePrice)}
						</TableCell>
						<TableCell className="text-center">
							{formatCurrency(priceWithVat)}
						</TableCell>
						<TableCell className="text-center">
							<span
								className={cn(
									"inline-flex min-w-[68px] items-center justify-center rounded-full px-2 py-1 text-xs font-semibold",
									getStockBadgeClasses(material),
								)}
							>
								{material.newStock}/{material.usedStock}
							</span>
						</TableCell>
						<TableCell className="text-center">{requested}</TableCell>
						<TableCell className="text-right">
							<div className="flex justify-end gap-2">
								<Button
									variant="secondary"
									size="sm"
									onClick={() => onEdit(material)}
									disabled={isLoading}
								>
									Editar
								</Button>
								<Button
									size="sm"
									className="bg-amber-500 text-white hover:bg-amber-400"
									onClick={() => onSell(material)}
									disabled={isLoading}
								>
									Vender
								</Button>
								<Button
									size="sm"
									className="bg-emerald-600 text-white hover:bg-emerald-500"
									onClick={() => onRequest(material)}
									disabled={isLoading}
								>
									Requisitar
								</Button>
							</div>
						</TableCell>
					</TableRow>
				);
			})}
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead className="w-[90px]">Cod.</TableHead>
						<TableHead>Descrição</TableHead>
						<TableHead className="w-[90px] text-center">Caixa</TableHead>
						<TableHead className="w-[110px] text-center">Preço</TableHead>
						<TableHead className="w-[140px] text-center">
							Preço c/ IVA
						</TableHead>
						<TableHead className="w-[90px] text-center">Stock</TableHead>
						<TableHead className="w-[90px] text-center">Aguarda</TableHead>
						<TableHead className="w-[240px] text-right">Ações</TableHead>
					</TableRow>
				</TableHeader>
				{isLoading
					? renderSkeleton()
					: data.length > 0
						? renderRows()
						: renderEmpty()}
			</Table>
		</div>
	);
}
